import { createContext, useContext } from 'react';
import type { WorkflowOption } from '../trigger/hooks';
import type { NestedHierarchyEditor } from './use-nested-workflow-hierarchy-edits';

export type OpenNestedWorkflow = (params: {
  workflowId: string;
  name: string | null;
}) => void;

/**
 * What the tree's cards and boxes are allowed to do.
 *
 * Reached by context rather than through node data: React Flow node data has to
 * stay plain (it is compared and cloned), so a card cannot carry callbacks.
 *
 * One context rather than one per capability. Both of these are provided by the
 * same host at the same time and consumed by neighbouring parts of the same
 * canvas, so splitting them only bought an extra provider to nest and an extra
 * import to keep in step.
 */
export type NestedTreeCapabilities = {
  /** Absent when there is no artifact stack to open a workflow into. */
  readonly openWorkflow: OpenNestedWorkflow | null;
  /**
   * Folds one workflow's own steps away, leaving its box and everything it runs.
   * Absent on a surface that draws the tree at one fixed shape.
   */
  readonly setCollapsed: ((key: string, collapsed: boolean) => void) | null;
  /** Absent when the tree is read-only. */
  readonly editing: {
    readonly editor: NestedHierarchyEditor;
    /** Every workflow that can be run as a step, for the target pickers. */
    readonly workflows: readonly WorkflowOption[];
  } | null;
  /** Whether a card opens a read-only config inspector when clicked. */
  readonly canInspectNodes: boolean;
};

const NestedTreeCapabilitiesContext =
  createContext<NestedTreeCapabilities | null>(null);

export const NestedTreeCapabilitiesProvider =
  NestedTreeCapabilitiesContext.Provider;

/** Null outside a provider, which reads as "no capabilities" rather than throwing. */
function useNestedTreeCapabilities(): NestedTreeCapabilities | null {
  return useContext(NestedTreeCapabilitiesContext);
}

/** The editing capability alone, for the affordances that only need that. */
export function useNestedTreeEditing(): NestedTreeCapabilities['editing'] {
  return useNestedTreeCapabilities()?.editing ?? null;
}

/** The open-a-workflow capability alone. */
export function useOpenNestedWorkflow(): OpenNestedWorkflow | null {
  return useNestedTreeCapabilities()?.openWorkflow ?? null;
}

/** Whether cards open the read-only inspector, for the step cards' cursor. */
export function useCanInspectNestedNodes(): boolean {
  return useNestedTreeCapabilities()?.canInspectNodes ?? false;
}

/**
 * The flow id of the card whose inspector is open, so that card can ring itself.
 *
 * A context of its own rather than a field on the capabilities, so opening the
 * inspector only re-renders the cards that read it, not every control on the
 * canvas.
 */
const NestedInspectedNodeContext = createContext<string | null>(null);

export const NestedInspectedNodeProvider = NestedInspectedNodeContext.Provider;

export function useNestedInspectedNodeId(): string | null {
  return useContext(NestedInspectedNodeContext);
}

/** The fold-a-group capability alone, for the group headers. */
export function useSetNestedGroupCollapsed():
  | ((key: string, collapsed: boolean) => void)
  | null {
  return useNestedTreeCapabilities()?.setCollapsed ?? null;
}
